// client/src/hooks/useSectionData.js
// Shared data-loading hook for the review section pages.
//
// Responsibilities:
//   * Initial fetch on mount (and whenever `deps` change).
//   * Silent background re-fetch when the app heartbeat (hooks/useHeartbeat.js)
//     reports a change by someone else in this section, an NLC mark, or a rebuilt
//     shared snapshot — pass `heartbeat: { sections, period }`. No loading
//     spinner; state is swapped in place.
//   * Optional timed re-fetch every `pollMs` (default: off with a heartbeat,
//     5 minutes without one).
//   * Tracks `lastRefreshedAt`, and with a heartbeat `freshness` (the age of the
//     shared snapshot behind the page) for the "Data as of" stamp.
//   * Exposes `refetch` / `setData` so callers can force a refresh or apply
//     optimistic patches.
//
// `fetcher` should be a stable async function (returns the parsed response).
// If its identity changes on every render, wrap it in useCallback.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useHeartbeat } from './useHeartbeat';

const DEFAULT_POLL_MS = 5 * 60 * 1000;

export function useSectionData(fetcher, { pollMs, deps = [], reconcile = null, heartbeat = null } = {}) {
  const pollEvery = pollMs ?? (heartbeat ? 0 : DEFAULT_POLL_MS);
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const mounted    = useRef(true);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  // Optional (prev, next, { startedAt }) => merged. Lets a page preserve in-flight optimistic
  // rows when a refetch/poll lands, so concurrent edits don't momentarily revert
  // to stale server data. Applied via functional setData so it sees the latest
  // local state (including patches made after this fetch started).
  const reconcileRef = useRef(reconcile);
  reconcileRef.current = reconcile;

  const doFetch = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    const startedAt = Date.now();
    try {
      const result = await fetcherRef.current();
      if (!mounted.current) return;
      const merge = reconcileRef.current;
      // `startedAt` lets a reconcile tell edits made after this fetch began (the response predates them).
      setData(prev => (merge && prev != null ? merge(prev, result, { startedAt }) : result));
      setLastRefreshedAt(new Date());
      setError(null);
    } catch (err) {
      if (!mounted.current) return;
      // A SILENT refresh (the poll / silentRefetch) must never disrupt the
      // screen. Swallow its error: keep the last good data and DON'T flip the
      // error state, so a flaky poll can't blank the page mid-interaction. Only
      // a foreground load (initial / explicit refetch) surfaces an error — and
      // the pages guard that on `!data`.
      if (!silent) setError(err.message || String(err));
    } finally {
      if (mounted.current && !silent) setLoading(false);
    }
  }, []);

  // Initial + deps-change fetch.
  useEffect(() => {
    mounted.current = true;
    doFetch();
    return () => { mounted.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  // Silent refetch when someone else changes this section / its snapshot moves.
  const freshness = useHeartbeat(heartbeat && {
    ...heartbeat,
    onChange: () => { doFetch({ silent: true }); },
  });

  // Optional timed polling.
  useEffect(() => {
    if (!pollEvery) return undefined;
    const id = setInterval(() => doFetch({ silent: true }), pollEvery);
    return () => clearInterval(id);
  }, [pollEvery, doFetch]);

  return {
    data, setData,
    loading, error,
    // `initialLoading` = the FIRST load only (no data yet). Guard the full-page
    // "Loading…" state on this, NOT on `loading` — otherwise a background poll,
    // a deps-change refetch, or a transient poll error blanks the whole screen
    // (unmounting open dropdowns) instead of updating in place. Standard pattern:
    //   if (initialLoading) return <Loading/>;
    //   if (error && !data) return <Error/>;
    initialLoading: loading && data == null,
    lastRefreshedAt,
    // Snapshot freshness from the heartbeat (null without one):
    // { builtAt, refreshing, active } — pass it to <LastRefreshed freshness>.
    freshness,
    refetch:       () => doFetch({ silent: false }),
    silentRefetch: () => doFetch({ silent: true }),
  };
}
